import { VDP_API_ENDPOINT, WIREMOCK_HOST } from 'constants/constant-urls';
import { VDPResponse, VDPSKU } from 'types/VDPTypes';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import { failSpan, startSpan, endSpan } from 'utilities/loggerv2/performanceSpans';

export class VDPService {
  private mockMode: boolean;

  private host: string;

  private apiPath: string;

  private apiEndpoint: string;

  private retryCount: number;

  private maxRetries: number;

  private timeout: number;

  constructor(mockMode = false, host = '', apiPath = VDP_API_ENDPOINT) {
    this.mockMode = mockMode;
    this.host = this.mockMode ? WIREMOCK_HOST : host;
    this.apiPath = apiPath;
    this.apiEndpoint = `${this.host}${this.apiPath}`;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.timeout = 1000;
  }

  private readonly logger = LoggerFactory.getLogger('VDP-api-logger');

  private formatError(error: unknown, defaultCode = 'UNKNOWN_ERROR'): Error {
    const errorObj = error instanceof Error ? error : new Error(String(error || 'Unknown error'));
    if (
      !Object.prototype.hasOwnProperty.call(errorObj, 'code') ||
      (errorObj as any).code === undefined ||
      (errorObj as any).code === null
    ) {
      (errorObj as any).code = defaultCode;
    }
    return errorObj;
  }

  public async getVDP(
    requestParams: { skus: Array<{ sku: string; productTypeCode: number }> },
    csrfToken: string,
    options: {
      vdpMockEnabled?: boolean;
      mockResponseName?: string;
      brand?: string;
      site: string;
    } = { site: 'very' },
  ): Promise<Array<VDPSKU> | void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    const spanId = startSpan('getVDP', {
      skus: requestParams.skus,
      csrfTokenProvided: !!csrfToken,
      brand: options.brand,
      vdpMockEnabled: options.vdpMockEnabled,
    });

    try {
      const headers: Record<string, string> = options.vdpMockEnabled
        ? {
            'x-mock-response': options.mockResponseName ?? 'vdp-api-success1Man',
            'x-brand': options.site,
          }
        : {
            'Content-Type': 'application/json',
            Accept: 'application/vnd.api+json',
            'x-enable-request-validator': 'true',
            'x-csrf-token': csrfToken,
            'x-brand': options.site,
          };

      const response = await fetch(this.apiEndpoint, {
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          skus: requestParams.skus,
        }),
        method: 'POST',
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status >= 500 && this.retryCount < this.maxRetries) {
          this.retryCount += 1;
          return await this.getVDP(requestParams, csrfToken, options);
        }

        const errorMessage = `HTTP error ${response.status}: ${response.statusText}`;
        const errorObj = new Error(errorMessage);

        Object.defineProperty(errorObj, 'code', {
          value: response.status.toString(),
          writable: true,
          enumerable: true,
        });

        const logger = await this.logger;

        if (logger && typeof logger.logWithCode === 'function') {
          logger.logWithCode(ErrorCodes.API_REQUEST_FAILED, errorObj, {
            errorCategory: 'VDP_API',
            errorCode: 'VDP_API_REQUEST_ERROR',
            service: 'VDP-api-logger',
          });
        }

        failSpan(spanId, {
          errorType: 'SERVICE_ERROR',
          message: errorObj.message || 'Unknown error',
          stack: errorObj.stack || '',
          errorCode: (errorObj as any).code || 'UNKNOWN_ERROR',
        });

        return undefined;
      }

      const { data } = (await response.json()) as VDPResponse;

      if (!data || !data.attributes || !data.attributes.skus) {
        const errorMessage = 'Invalid API response: Missing required data.';
        const errorObj = new Error(errorMessage);

        Object.defineProperty(errorObj, 'code', {
          value: 'INVALID_API_RESPONSE',
          writable: true,
          enumerable: true,
        });

        const logger = await this.logger;

        if (logger && typeof logger.logWithCode === 'function') {
          logger.logWithCode(ErrorCodes.API_INVALID_RESPONSE, errorObj);
        }

        failSpan(spanId, {
          errorType: 'INVALID_RESPONSE',
          response: data,
        });

        return undefined;
      }

      endSpan(spanId, { success: true });
      return data.attributes.skus;
    } catch (error) {
      if (this.retryCount < this.maxRetries) {
        this.retryCount += 1;
        return await this.getVDP(requestParams, csrfToken, options);
      }

      const errorObj = this.formatError(error, 'UNKNOWN_ERROR');

      const logger = await this.logger;

      if (logger && typeof logger.logWithCode === 'function') {
        logger.logWithCode(ErrorCodes.API_REQUEST_FAILED, errorObj);
      }

      failSpan(spanId, {
        errorType: 'SERVICE_ERROR',
        message: errorObj.message || 'Unknown error',
        stack: errorObj.stack || '',
        errorCode: (errorObj as any).code || 'UNKNOWN_ERROR',
      });

      return undefined;
    }
  }
}

export default VDPService;
